import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';
import CountryCityFields from './CountryCityFields';
import { resolveDefaultCountry } from '../utils/locationData';
import { isWhatsAppUrl, trackContact, prepareLeadSubmitTracking } from '../utils/metaPixel';
import { getSocialNetworks } from '../utils/socialNetworks';
import { getMediaUrl } from '../utils/mediaUrl';

const getContactInfoIcon = (type) => {
  switch (type) {
    case 'whatsapp': return 'fab fa-whatsapp';
    case 'phone': return 'fas fa-phone-alt';
    case 'email': return 'fas fa-envelope';
    case 'location':
    case 'address':
      return 'fas fa-map-marker-alt';
    default:
      return 'fas fa-info-circle';
  }
};

const getTrainingLocationIcon = (type) => {
  switch (type) {
    case 'primary': return 'fas fa-star';
    case 'secondary': return 'fas fa-map-marker-alt';
    default: return 'fas fa-building';
  }
};

const getMapEmbedUrl = (mapLink) => {
  if (!mapLink) return '';
  try {
    const url = new URL(mapLink);
    const query = url.searchParams.get('q');
    if (query) {
      return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
    }
  } catch {
    // fall through
  }
  if (mapLink.includes('output=embed')) return mapLink;
  return `${mapLink}${mapLink.includes('?') ? '&' : '?'}output=embed`;
};

const Contact = () => {
  const { getContent } = useContent();
  const pageHeader = getContent('contact.pageHeader') || {};
  const contactInfo = getContent('contact.contactInfo') || [];
  const contactForm = getContent('contact.contactForm') || {};
  const quickActions = getContent('contact.quickActions') || {};
  const trainingLocations = getContent('contact.trainingLocations') || {};
  const registrationLinks = getContent('contact.registrationLinks') || {};
  const faq = getContent('contact.faq') || {};
  const interactiveMap = getContent('contact.interactiveMap') || {};
  const socialNetworksSection = getContent('contact.socialNetworks') || {};
  const socialNetworkItems = getSocialNetworks(getContent);
  const contactCta = getContent('contact.cta') || {};
  const [selectedCountry, setSelectedCountry] = useState(
    () => resolveDefaultCountry(contactForm.defaultCountry)
  );
  const [cityKey, setCityKey] = useState(0);
  // State for FAQ accordion
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      city: formData.get('city'),
      country: formData.get('country'),
      training: formData.get('program'),
      experience: formData.get('experience'),
      message: formData.get('message')
    };

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://dentoxcellence-backend.dentoxcellence.workers.dev/api';
      const metaTracking = prepareLeadSubmitTracking({
        formationName: data.training,
        source: 'contact_page_form',
      });
      const response = await fetch(`${API_URL}/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...data, ...metaTracking })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setFormStatus({ 
          type: 'success', 
          message: 'Merci pour votre message ! Nous vous contacterons très bientôt.' 
        });
        e.target.reset();
      } else {
        throw new Error(result.error || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus({ 
        type: 'error', 
        message: 'Une erreur est survenue. Veuillez réessayer ou nous contacter directement.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Page Header */}
      <section className="page-header" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Hero Image Layer */}
        {pageHeader.heroImage && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            zIndex: 0
          }}>
            <img
              src={getMediaUrl(pageHeader.heroImage)}
              alt="Hero"
              style={pageHeader.heroImageSettings ? {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(calc(-50% + ${pageHeader.heroImageSettings.position.x}px), calc(-50% + ${pageHeader.heroImageSettings.position.y}px)) scale(${pageHeader.heroImageSettings.scale})`,
                transformOrigin: 'center center',
                width: 'auto',
                height: 'auto',
                minWidth: '100%',
                minHeight: '100%',
                maxWidth: 'none',
                objectFit: 'cover'
              } : {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
        )}
        
        {/* Gradient Overlay - Only if enabled */}
        {pageHeader.overlayEnabled !== false && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: pageHeader.overlayColor || '#58c4dc',
            opacity: pageHeader.overlayOpacity ?? 0.85,
            zIndex: 1
          }}></div>
        )}
        
        {/* Content */}
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="page-header-content">
            <h1>{pageHeader.title || 'Contactez-nous'}</h1>
            <p>{pageHeader.subtitle || 'Nous sommes toujours disponibles pour répondre à vos questions et vous accompagner dans votre parcours de formation'}</p>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="contact-info" id="contact-info">
        <div className="container">
          <div className="contact-grid">
            {contactInfo.map((info, index) => (
                <div key={index} className={`contact-card ${info.type}`}>
                  <div className="contact-card-icon">
                    <i className={info.icon || getContactInfoIcon(info.type)}></i>
                  </div>
                  <h3>{info.title}</h3>
                  <p>{info.description}</p>
                  {info.link ? (
                    <a
                      href={info.link}
                      className="contact-link"
                      target={info.link.includes('wa.me') ? '_blank' : '_self'}
                      rel={info.link.includes('wa.me') ? 'noopener noreferrer' : undefined}
                      onClick={
                        isWhatsAppUrl(info.link)
                          ? () => trackContact({ source: 'contact_info', label: info.title })
                          : undefined
                      }
                    >
                      {info.value}
                    </a>
                  ) : (
                    <span className="contact-link">{info.value}</span>
                  )}
                  <span className="availability">
                    <i className="fas fa-clock"></i>
                    {info.availability}
                  </span>
                </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="contact-form-section" id="contact-form">
        <div className="container">
          <div className="form-container">
            <div className="form-header">
              <h2>{contactForm.title || 'Demander des Informations'}</h2>
              <p>{contactForm.subtitle || 'Remplissez ce formulaire et nous vous contacterons rapidement pour discuter de vos besoins de formation'}</p>
            </div>
            
            <form className="contact-form" id="contactForm" onSubmit={handleSubmit}>
              {formStatus.message && (
                <div className={`form-alert ${formStatus.type}`}>
                  <i className={`fas fa-${formStatus.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                  {formStatus.message}
                </div>
              )}
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name"><i className="fas fa-user"></i> Nom complet *</label>
                  <input type="text" id="name" name="name" required />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email"><i className="fas fa-envelope"></i> Email *</label>
                  <input type="email" id="email" name="email" required />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone"><i className="fas fa-phone-alt"></i> Téléphone *</label>
                  <input type="tel" id="phone" name="phone" required />
                </div>
                <CountryCityFields
                  contactForm={contactForm}
                  renderCity={false}
                  activeCountry={selectedCountry}
                  onActiveCountryChange={(value) => {
                    setSelectedCountry(value);
                    setCityKey((k) => k + 1);
                  }}
                />
              </div>

              <CountryCityFields
                contactForm={contactForm}
                renderCountry={false}
                cityName="city"
                cityId="city"
                activeCountry={selectedCountry}
                cityResetKey={cityKey}
              />
              
              <div className="form-group">
                <label htmlFor="program"><i className="fas fa-graduation-cap"></i> Formation d'intérêt</label>
                <select id="program" name="program">
                  <option value="">Sélectionner une formation</option>
                  {(contactForm.subjects || []).map((subject, index) => (
                    <option key={index} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="experience"><i className="fas fa-briefcase"></i> Années d'expérience</label>
                <select id="experience" name="experience">
                  <option value="">Sélectionner votre expérience</option>
                  {(contactForm.experienceOptions || []).map((option, index) => (
                    <option key={index} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="message"><i className="fas fa-comment-alt"></i> Message</label>
                <textarea id="message" name="message" rows="5" placeholder={contactForm.messagePlaceholder || "Décrivez vos besoins spécifiques, questions ou demandes particulières..."}></textarea>
              </div>
              
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input type="checkbox" id="newsletter" name="newsletter" />
                  <span className="checkmark"></span>
                  <i className="fas fa-bell" style={{ marginRight: '8px', color: '#3b82f6' }}></i>
                  {contactForm.newsletterText || 'Je souhaite recevoir les actualités et offres spéciales de DentoXcellence'}
                </label>
              </div>
              
              <button type="submit" className="btn btn-primary submit-btn" disabled={isSubmitting}>
                <i className="fas fa-paper-plane"></i>
                {isSubmitting ? 'Envoi en cours...' : (contactForm.submitText || 'Envoyer la Demande')}
              </button>
            </form>

            <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <p style={{ marginBottom: '0.75rem' }}>
                {contactForm.testimonialPrompt || 'Vous avez suivi une formation DentoXcellence ?'}
              </p>
              <Link to="/temoignages#submit" className="btn btn-outline">
                <i className="fas fa-comment-dots"></i>
                {contactForm.testimonialLinkText || 'Laisser un témoignage'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="quick-actions" id="quick-actions">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{quickActions.title || 'Actions Rapides'}</h2>
            <p className="section-subtitle">
              {quickActions.subtitle || 'Choisissez la méthode de contact qui vous convient le mieux'}
            </p>
          </div>
          
          <div className="actions-grid">
            {(quickActions.actions || []).map((action, index) => (
              <div key={index} className={`action-card ${action.type}`}>
              <div className="action-icon">
                  <i className={action.icon}></i>
              </div>
                <h3>{action.title}</h3>
                <p>{action.description}</p>
                <a
                  href={action.link}
                  className={action.buttonClass}
                  target={action.link.includes('wa.me') ? '_blank' : '_self'}
                  rel={action.link.includes('wa.me') ? 'noopener noreferrer' : undefined}
                  onClick={
                    isWhatsAppUrl(action.link)
                      ? () => trackContact({ source: 'contact_quick_action', label: action.title })
                      : undefined
                  }
                >
                  <i className={action.buttonIcon}></i>
                  {action.buttonText}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Locations */}
      <section className="training-locations" id="locations">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{trainingLocations.title || 'Lieux de Formation'}</h2>
            <p className="section-subtitle">
              {trainingLocations.subtitle || 'Nos formations se déroulent dans les principales villes d\'Algérie'}
            </p>
          </div>
          
          <div className="locations-grid">
            {(trainingLocations.locations || []).map((location, index) => (
              <div key={index} className={`location-card ${location.type}`}>
              <div className="location-icon">
                <i className={getTrainingLocationIcon(location.type)}></i>
              </div>
                <h3>{location.city}</h3>
                <p className="location-type">{location.description}</p>
                <p>{location.address}</p>
              <div className="location-features">
                  {(location.features || []).map((feature, featureIndex) => (
                    <span key={featureIndex} className="feature">• {feature}</span>
                  ))}
            </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Links */}
      <section className="registration-section" id="registration">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{registrationLinks.title || 'Liens d\'Inscription Rapide'}</h2>
            <p className="section-subtitle">
              {registrationLinks.subtitle || 'Choisissez votre formation et inscrivez-vous immédiatement'}
            </p>
          </div>

          <div className="registration-grid">
            {(registrationLinks.programs || []).map((program, index) => (
              <div key={index} className={`registration-card ${program.popular ? 'popular' : ''}`}>
                <div className="registration-header">
                  <h3>{program.name}</h3>
                  {program.popular && <span className="popular-badge">Populaire</span>}
                </div>
                <p>{program.description}</p>
                <div className="program-details">
                  <div className="detail">
                    <i className="fas fa-clock"></i>
                    <span>{program.duration}</span>
              </div>
                  <div className="detail">
                    <i className="fas fa-tag"></i>
                    <span>{program.price}</span>
            </div>
              </div>
                <a href="/formations#registration-form" className="btn btn-primary">
                <i className="fas fa-user-plus"></i>
                  S'inscrire
              </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{faq.title || 'Questions Fréquentes'}</h2>
            <p className="section-subtitle">
              {faq.subtitle || 'Trouvez rapidement les réponses à vos questions les plus courantes'}
            </p>
          </div>
          
          <div className="faq-container">
            {(faq.questions || []).map((item, index) => (
              <div key={index} className={`faq-item ${openFaqIndex === index ? 'active' : ''}`}>
                <div className="faq-question" onClick={() => toggleFaq(index)}>
                  <h3>{item.question}</h3>
                  <i className={`fas fa-chevron-${openFaqIndex === index ? 'up' : 'down'}`}></i>
                </div>
                <div className="faq-answer" style={{ display: openFaqIndex === index ? 'block' : 'none' }}>
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Map */}
      {(interactiveMap.title || interactiveMap.address || interactiveMap.mapLink) && (
      <section className="interactive-map" id="map">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{interactiveMap.title || 'Notre Localisation'}</h2>
            <p className="section-subtitle">
              {interactiveMap.subtitle || 'Trouvez-nous facilement'}
            </p>
          </div>

          <div className="map-container">
            <div className="map-placeholder">
              {interactiveMap.mapLink && (
                <div className="map-embed">
                  <iframe
                    src={getMapEmbedUrl(interactiveMap.mapLink)}
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="DentoXcellence Location"
                  />
                </div>
              )}
              <div className="map-overlay">
                <div className="map-info">
                  <h3>{interactiveMap.title || 'Adresse'}</h3>
                  {interactiveMap.address && <p>{interactiveMap.address}</p>}
                  <div className="location-details">
                    {interactiveMap.address && (
                      <div className="location-item">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{interactiveMap.address}</span>
                      </div>
                    )}
                    {interactiveMap.directions && (
                      <div className="location-item">
                        <i className="fas fa-directions"></i>
                        <span>{interactiveMap.directions}</span>
                      </div>
                    )}
                  </div>
                  {interactiveMap.mapLink && (
                    <div className="map-actions">
                      <a
                        href={interactiveMap.mapLink}
                        className="btn btn-primary"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fas fa-map"></i>
                        Ouvrir dans Google Maps
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Social Networks */}
      <section className="social-networks">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{socialNetworksSection.title || 'Suivez-nous sur les Réseaux Sociaux'}</h2>
            <p className="section-subtitle">
              {socialNetworksSection.subtitle || 'Restez informé de nos dernières actualités et formations'}
            </p>
          </div>
          
          <div className="social-grid">
            {socialNetworkItems.map((network, index) => (
              <div key={index} className={`social-card ${(network.name || '').toLowerCase()}`}>
              <div className="social-icon">
                  <i className={network.icon}></i>
              </div>
                <h3>{network.name}</h3>
                <p>{network.description}</p>
                <a href={network.url} className="btn btn-social" target="_blank" rel="noopener noreferrer">
                  <i className={network.icon}></i>
                Suivre
              </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>{contactCta.title || 'Prêt à Transformer Votre Pratique ?'}</h2>
            <p>{contactCta.subtitle || 'Rejoignez plus de 500 dentistes qui ont déjà fait confiance à DentoXcellence'}</p>
            <div className="cta-buttons">
              {(contactCta.buttons || []).map((button, index) => (
                <a key={index} href={button.link} className={`btn btn-${button.type}`} target={button.target} rel={button.target === '_blank' ? 'noopener noreferrer' : undefined}>
                  <i className={button.icon}></i>
                  {button.text}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;